import React, { useState } from "react";
import styled from "styled-components";
import { Button, Col, Form, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import Loading from "components/Loading";
import EasyFeedbackModal from "components/EasyFeedbackModal";
import NewsletterContainer from "./Components/NewsletterContainer";
import { NewsletterHeaderTitle } from "./Components/NewsletterHeaderTitle";
import BackButton from "./Components/BackButton";
import CreateNewsletterService from "./CreateNewsletterService";
import { URL_PAGES } from "utils/constants";
import DefaultImage from "images/default_image.svg";
import DownloadIcon from "images/download_icon.svg";
import DeleteIcon from "images/delete_icon.svg";

const NewsletterPlaceholderImage = styled.img`
    width: fit-content;
    height: 300px;
    margin: auto
`

const NewsletterImage = styled.img`
    width: fit-content;
    height: 500px;
    margin: auto;
    display: block
`

const NewsletterVideo = styled.video`
    width: fit-content;
    height: 500px;
    margin: auto;
    display: block
`

type ChangeEvent = React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>;

interface NewsletterFormParams {
    title: string;
    categoryIconName: string;
    content: string;
    mediaFiles: File[];
    mediaPreviewUrls: string[];
    accessRestrict: string;
    emailRestrict: string[];
}

interface NewsletterFeedback {
    title: string;
    message: string;
    show: boolean;
    clickListener: () => void;
}

const initialFormParams: NewsletterFormParams = {
    title: "",
    categoryIconName: "IC_COMUNICACAO",
    content: "",
    mediaFiles: [],
    mediaPreviewUrls: [],
    accessRestrict: "TODOS",
    emailRestrict: []
};

export default function Newsletter() {
    const navigate = useNavigate();
    const [formParams, setFormParams] = useState<NewsletterFormParams>(initialFormParams);
    const [isFormValid, setFormValid] = useState<boolean>(false);
    const [isLoading, setLoading] = useState<boolean>(false);
    const [feedback, setFeedback] = useState<NewsletterFeedback>();

    const handleChange = (event: ChangeEvent) => {
        const { name, value } = event.target;
        setFormParams({ ...formParams, [name]: value });
    };

    const handleMediaChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event?.target?.files;

        if (formParams.mediaPreviewUrls.length > 0) {
            formParams.mediaPreviewUrls.forEach((previewUrl) => URL.revokeObjectURL(previewUrl));
        }

        if (files && files.length > 0) {
            const file = files[0];
            const mediaPreviewUrls = [URL.createObjectURL(file)];
            setFormParams({ ...formParams, mediaFiles: [file], mediaPreviewUrls });
            return;
        }

        setFormParams({ ...formParams, mediaFiles: [], mediaPreviewUrls: [] });
    };

    const onSubmitForm = async (event: React.BaseSyntheticEvent) => {
        event.preventDefault();
        event.stopPropagation();

        const form = event.currentTarget as HTMLFormElement;
        if (form.checkValidity() === true) {
            await createNewsletter();
        }

        setFormValid(true);
    };

    const createNewsletter = async () => {
        const newsletterService = new CreateNewsletterService();
        const categoryNameMap = {
            "IC_FINANCEIRO": "Financeiro",
            "IC_RH": "RH",
            "IC_TH": "TH",
            "IC_COMUNICACAO": "Comunicação"
        }

        setLoading(true);
        try {
            await newsletterService.createNewsletter(
                formParams.title,
                formParams.categoryIconName,
                formParams.content,
                formParams.mediaFiles,
                formParams.accessRestrict,
                formParams.emailRestrict,
                categoryNameMap[formParams.categoryIconName as keyof typeof categoryNameMap]
            );

            setFeedback({
                title: "Post criado com sucesso",
                message: "A postagem foi publicada com sucesso, clique abaixo para voltar.",
                show: true,
                clickListener: () => navigate(URL_PAGES.HOME)
            });
        } catch (error: AxiosError | any) {
            const errorMessage = error?.response?.data?.message;
            setFeedback({
                title: "Houve um erro ao criar o post",
                message: errorMessage ?? "Tivemos um problema ao salvar a postagem. Por favor, tente novamente.",
                show: true,
                clickListener: () => setFeedback(undefined)
            });
        } finally {
            setLoading(false);
        }
    };

    function UploadButton(props: { onChange: (element: any) => void, className?: string }) {
        return (
            <Button variant="secondary" className={props?.className}>
                <label htmlFor="mediaSelected" style={{ cursor: "pointer" }}>
                    <img src={DownloadIcon} alt="Selecionar" style={{ width: "30px" }} className="m-1" />
                    <div>Selecionar arquivo</div>
                    <input
                        type="file"
                        id="mediaSelected"
                        accept="image/*,video/*"
                        hidden
                        onChange={props.onChange}
                    />
                </label>
            </Button>
        );
    }

    return (
        <NewsletterContainer>
            <Loading isLoading={isLoading} />
            <NewsletterHeaderTitle
                title="Criar Post Corporativo"
                description="Preencha os dados abaixo para publicar uma nova atualização no feed da TQI."
            />
            <br />
            <BackButton onClick={() => navigate(URL_PAGES.HOME)} />
            <br />
            <Form.Label>Público Alvo:</Form.Label>
            <Row className="mb-3">
                <div key={`inline-radio`} className="mb-3">
                    <Form.Check
                        inline
                        label="Todos os usuários"
                        name="group1"
                        type="radio"
                        id={`inline-radio-1`}
                        checked={formParams.accessRestrict === "TODOS"}
                        onChange={() => setFormParams({ ...formParams, accessRestrict: "TODOS", emailRestrict: [] })}
                    />
                    <Form.Check
                        inline
                        label="Todos os CLTs"
                        name="group1"
                        type="radio"
                        id={`inline-radio-2`}
                        checked={formParams.accessRestrict === "CLT_MENSALISTA"}
                        onChange={() => setFormParams({ ...formParams, accessRestrict: "CLT_MENSALISTA", emailRestrict: [] })}
                    />
                    <Form.Check
                        inline
                        name="group1"
                        label="Todos os PJs Horistas"
                        type="radio"
                        id={`inline-radio-3`}
                        checked={formParams.accessRestrict === "PJ_MENSALISTA"}
                        onChange={() => setFormParams({ ...formParams, accessRestrict: "PJ_MENSALISTA", emailRestrict: [] })}
                    />
                    <Form.Check
                        inline
                        name="group1"
                        label="Todos os PJs Mensalistas"
                        type="radio"
                        id={`inline-radio-4`}
                        checked={formParams.accessRestrict === "PJ_HORISTA"}
                        onChange={() => setFormParams({ ...formParams, accessRestrict: "PJ_HORISTA", emailRestrict: [] })}
                    />
                    <Form.Check
                        inline
                        name="group1"
                        label="Individual"
                        type="radio"
                        id={`inline-radio-5`}
                        checked={formParams.accessRestrict === "BLOQUEADO"}
                        onChange={() => setFormParams({ ...formParams, accessRestrict: "BLOQUEADO", emailRestrict: [] })}
                    />
                </div>
            </Row>
            <Row className="mb-3">
                <Form.Group as={Col} md="12" controlId="emailRestrict">
                    <Form.Label>E-mails dos colaboradores:</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="E-mails dos colaboradores separados por vírgula"
                        disabled={formParams.accessRestrict !== "BLOQUEADO"}
                        value={formParams.emailRestrict.join(",")}
                        onChange={(e) => setFormParams({ ...formParams, emailRestrict: e.target.value.split(",").map((v) => v.trim()) })}
                    />
                </Form.Group>
            </Row>
            <Form noValidate validated={isFormValid} onSubmit={onSubmitForm} className="row g-3">
                <Form.Group className="col-12">
                    <Form.Label>Título da postagem:</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Digite o título da postagem"
                        name="title"
                        value={formParams.title}
                        onChange={handleChange}
                        required
                    />
                    <Form.Control.Feedback type="invalid">
                        Título da postagem é obrigatório.
                    </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="col-12 col-md-6">
                    <Form.Label>Categoria:</Form.Label>
                    <Form.Select
                        name="categoryIconName"
                        value={formParams.categoryIconName}
                        onChange={handleChange}
                    >
                        <option value="IC_FINANCEIRO">Financeiro</option>
                        <option value="IC_RH">RH</option>
                        <option value="IC_TH">TH</option>
                        <option value="IC_COMUNICACAO">Comunicação</option>
                    </Form.Select>
                </Form.Group>

                <Form.Group className="col-12">
                    <Form.Label>Conteúdo:</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={6}
                        placeholder="Escreva o conteúdo da postagem"
                        name="content"
                        value={formParams.content}
                        onChange={handleChange}
                        maxLength={5000}
                    />
                    <Form.Text muted>
                        {formParams.content.length}/5000 caracteres
                    </Form.Text>
                </Form.Group>

                <Form.Group className="col-12">
                    <Form.Label><strong>Mídia:</strong></Form.Label>
                    <Row className="d-flex justify-content-center g-2">
                        <Col xs={12}>
                            {formParams.mediaFiles.length > 0 ? (
                                formParams.mediaFiles.map((file, index) => (
                                    <div className="mb-3" key={`${file.name}-${index}`}>
                                        {file.type.startsWith("video") ? (
                                            <NewsletterVideo
                                                controls
                                                src={formParams.mediaPreviewUrls[index]}
                                            />
                                        ) : (
                                            <NewsletterImage
                                                src={formParams.mediaPreviewUrls[index]}
                                                alt={`Preview ${index + 1}`}
                                            />
                                        )}
                                    </div>
                                ))
                            ) : (
                                <NewsletterPlaceholderImage
                                    className="row"
                                    src={DefaultImage}
                                    alt="Placeholder de mídia"
                                />
                            )}
                        </Col>
                        <Col lg={2} sm={4}>
                            <Row className="d-flex justify-content-center">
                                <UploadButton className="col-sm-10" onChange={handleMediaChanged} />
                            </Row>
                        </Col>
                        <Col lg={2} sm={4}>
                            <Row className="d-flex justify-content-center">
                                <Button
                                    className="col-sm-10"
                                    onClick={() => setFormParams({ ...formParams, mediaFiles: [], mediaPreviewUrls: [] })}
                                    variant="outline-danger">
                                    <img src={DeleteIcon} alt="Remover" style={{ width: "25px" }} className="m-1" />
                                    <div>
                                        Remover arquivo
                                    </div>
                                </Button>
                            </Row>
                        </Col>
                    </Row>
                </Form.Group>

                <Row className="d-flex justify-content-end g-2 mt-3">
                    <Col xs="auto">
                        <Button variant="secondary" type="button" onClick={() => navigate(URL_PAGES.HOME)}>
                            Voltar
                        </Button>
                    </Col>
                    <Col xs="auto">
                        <Button variant="primary" type="submit">
                            Publicar
                        </Button>
                    </Col>
                </Row>
            </Form>

            <EasyFeedbackModal
                show={feedback?.show ?? false}
                title={feedback?.title}
                description={feedback?.message}
                listener={() => feedback?.clickListener()} />
        </NewsletterContainer>
    );
}
